'use client'

import { useEffect, useRef, useState } from 'react'

const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/

interface Props {
  encodedTo: string
  blank?: boolean
  ariaLabel?: string
  className?: string
}

export interface HideFromCrawlerProps extends Props {
  type: 'hidden'
}

export interface SEOLinkProps extends Props {
  type: 'crawler'
  crawlerLink: string
}

const SeoLink: React.FC<React.PropsWithChildren<HideFromCrawlerProps | SEOLinkProps>> = (props) => {
  const { children, blank = false, encodedTo, ariaLabel, type, className } = props

  // Check that the url is encoded and not seen by Google
  if (!base64regex.test(encodedTo)) {
    throw new Error(`The SEO url is not base64 encoded: ${encodedTo}`)
  }

  const initialTo = type === 'crawler' ? props.crawlerLink : '#'

  const [to, setTo] = useState(initialTo)
  const linkElRef = useRef<HTMLAnchorElement | null>(null)

  const blankProps = blank ? { target: `_blank`, rel: 'noopener' } : {}
  const ariaLabelProps = ariaLabel ? { 'aria-label': ariaLabel } : {}

  useEffect(() => {
    if (!linkElRef.current) return

    // For the linter - it was complaining that the ref value could change, and to save it locally.
    const linkRefValue = linkElRef.current

    const handleFirstInteraction = () => setTo(atob(encodedTo))

    // Not yet using the hooke for the first interaction, because ideally we wanted that hook to be passed an element / document / etc.
    // and was having trouble passing the link ref, since it is initially null.
    linkRefValue.addEventListener('touchstart', handleFirstInteraction, { once: true, passive: true })
    linkRefValue.addEventListener('mousedown', handleFirstInteraction, { once: true, passive: true })
    linkRefValue.addEventListener('onfocus', handleFirstInteraction, { once: true, passive: true })
    linkRefValue.addEventListener('mousemove', handleFirstInteraction, { once: true, passive: true })
    linkRefValue.addEventListener('keydown', handleFirstInteraction, { once: true, passive: true })

    return () => {
      if (!linkRefValue) return
      linkRefValue.removeEventListener('touchstart', handleFirstInteraction)
      linkRefValue.removeEventListener('mousedown', handleFirstInteraction)
      linkRefValue.removeEventListener('onfocus', handleFirstInteraction)
      linkRefValue.removeEventListener('mousemove', handleFirstInteraction)
      linkRefValue.removeEventListener('keydown', handleFirstInteraction)
    }
  }, [])

  return (
    <a ref={linkElRef} href={to} {...blankProps} {...ariaLabelProps} className={className}>
      {children}
    </a>
  )
}

export default SeoLink
